import React, {
  useCallback,
  useTransition,
  useDeferredValue,
  Suspense,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import CultureTreeSelect from '@client/components/ui/select/CultureTreeSelect';
import CultureErrorBoundary from '../CultureErrorBoundary';

const CULTURE_ENDPOINT = '/api/cultures/tree/calculator/';
const CALCULATOR_FILTER_PARAMS = { filter_calculator: true };

/**
 * Step 1 - Cultures Component
 * Tree-based culture selection using CultureTreeSelect
 * Displays ARLA hierarchy and allows multi-select (or single-select for edit mode)
 *
 * React 18 Suspense Solution:
 * - useTransition: Marks onChange updates as non-urgent transitions
 * - useDeferredValue: Defers value prop updates to TreeSelect until data is ready
 *
 * @param {Object} props
 * @param {Array} props.selectedCultures - Array of selected culture IDs
 * @param {Function} props.onSelectCultures - Callback when cultures are selected
 * @param {boolean} props.singleSelect - If true, only allow single culture selection (edit mode)
 */
const Step1Cultures = React.memo(
  ({
    selectedCultures = [],
    onSelectCultures,
    enrichedCultures = null,
    singleSelect = false,
  }) => {
    const { t } = useTranslation('riskCalculator');
    const [_isPending, startTransition] = useTransition();

    // Defer the value prop to TreeSelect to prevent Suspense errors
    // This allows React to show stale values while new data loads
    const deferredCultures = useDeferredValue(selectedCultures);

    /**
     * Handle culture selection change
     * Use startTransition to prevent Suspense errors when updating tree values
     * Pass enriched nodes for tags display
     */
    const handleCultureChange = useCallback(
      (selectedValues, enrichedNodes) => {
        const values = Array.isArray(selectedValues) ? selectedValues : [];
        startTransition(() => {
          onSelectCultures(values, enrichedNodes || []);
        });
      },
      [onSelectCultures]
    );

    return (
      <div className="flex flex-col justify-center items-center gap-4 sm:gap-6 mx-auto w-full px-2 sm:px-0">
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('cultures_title')}
        </h3>
        <p className="text-xs sm:text-sm text-[#000] self-start">
          {t('cultures_description')}
        </p>
        <p className="text-xs sm:text-sm text-[#000] self-start">
          {t('cultures_arla_info')}
        </p>

        {/* Culture Tree Select - Wrapped in Error Boundary and Suspense */}
        <CultureErrorBoundary>
          <Suspense
            fallback={<Spin tip="Chargement de l'arbre des cultures..." />}
          >
            <div className="step-select-container-no-overflow risk-calculator-culture-select">
              <CultureTreeSelect
                endpoint={CULTURE_ENDPOINT}
                treeCheckable={!singleSelect}
                labelField="french_name"
                isMulti={!singleSelect}
                placeholder={t('cultures_search_placeholder')}
                label={t('culture_label')}
                value={deferredCultures}
                onChange={handleCultureChange}
                languageFields={{
                  fr: 'french_name',
                  en: 'english_name',
                  la: 'latin_name',
                }}
                languageLabels={{
                  fr: 'Français',
                  en: 'Anglais',
                  la: 'Latin',
                }}
                defaultLanguage="fr"
                showLanguageSelector={true}
                style={{ width: '100%' }}
                checkStrictly={false}
                maxCount={singleSelect ? undefined : 10}
                enrichedCultures={enrichedCultures}
                filterParams={CALCULATOR_FILTER_PARAMS}
                maxTagCount={2}
                showPfiTag={false}
              />
            </div>
          </Suspense>
        </CultureErrorBoundary>
      </div>
    );
  }
);

Step1Cultures.displayName = 'Step1Cultures';

export default Step1Cultures;
